import React, { useState } from 'react';
import { Image, Spin, Upload } from 'antd';
import OpenNewTab from '@assets/svgs/open-new-tab-icon.svg';
import { downloadCvUrl, uploadCV } from '@services/applicants/applicants-http.service';
import useNotification from '@hooks/notification/useNotification';
import StateEnum from '@constants/state.enum';
import UiButton from '@components/ui/button/UiButton';
import { changeDateFormat } from '@services/date/date-format';
import styles from './CvCard.module.scss';
import { useTranslation } from 'next-i18next';
import { RcFile } from 'antd/lib/upload';

type CvCardProps = {
  cv?: string;
  createdDate: Date;
  applicantId: number;
  fetchApplicant: Function;
};
const CvCard: React.FC<CvCardProps> = ({ cv, createdDate, applicantId, fetchApplicant }) => {
  const { t: translate } = useTranslation('applicant');
  const [cvUrlLoading, setCvUrlLoading] = useState<boolean>(false);
  const [uploadedFile, setUploadedFile] = useState<FormData>();

  const [generalError] = useNotification(
    StateEnum.ERROR,
    translate('common:smth_went_wrong'),
    translate('common:oops_wrong'),
  );

  const [uploadCvMessage] = useNotification(
    StateEnum.WARNING,
    translate('unable_upload_cv'),
    translate('cv_be_smaller'),
  );

  const [successCvUpload] = useNotification(StateEnum.SUCCESS, translate('add_cv'), translate('successfully_add_cv'));

  const getCvUrl = async (cvId: string) => {
    setCvUrlLoading(true);
    try {
      const cvUrlElem: string = await downloadCvUrl(cvId);
      setCvUrlLoading(false);
      return cvUrlElem;
    } catch (e: any) {
      setCvUrlLoading(false);
      return '';
    }
  };

  const setupFileUploaded = (val: any) => {
    if (val.size < 1572864) {
      const data = new FormData();
      data.append('file', val.originFileObj);
      setUploadedFile(data);
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isLt2M = file.size / 1024 / 1024 < 1.5;
    if (!isLt2M) uploadCvMessage('Unable to upload your CV');
    return isLt2M;
  };

  if (cv) {
    return (
      <div
        role="button"
        className={styles.wrapCvCard}
        onClick={async (event) => {
          event.stopPropagation();
          const cvTempUrl: string = await getCvUrl(cv);
          if (cvTempUrl) {
            return window.open(cvTempUrl, '_blank', 'noopener,noreferrer');
          }
          return generalError();
        }}
        onKeyDown={async (event) => {
          event.stopPropagation();
          const cvTempUrl: string = await getCvUrl(cv);
          if (cvTempUrl) {
            return window.open(cvTempUrl, '_blank', 'noopener,noreferrer');
          }
          return generalError();
        }}
        tabIndex={0}
      >
        <div className={styles.wrapCvInfo}>
          <div className={styles.cvTitle}>
            <div className={`main-body-text ${styles.cvInfoText}`}>{translate('cv')}</div>
            <div className={`small-body-text ${styles.cvInfoDate}`}>{changeDateFormat(createdDate)}</div>
          </div>
          {cvUrlLoading ? (
            <div className={styles.wrapDummyCvLoader}>
              <Spin />
            </div>
          ) : (
            <UiButton size="small" type="ghost">
              <OpenNewTab />
            </UiButton>
          )}
        </div>
        <div className={styles.wrapCvIcon}>
          <Image src="/assets/images/blurred-profile.png" preview={false} alt="Dummy cv" />
        </div>
      </div>
    );
  }
  return (
    <Upload
      onChange={(value) => setupFileUploaded(value.file)}
      customRequest={async () => {
        try {
          await uploadCV(applicantId.toString(), uploadedFile!);
          fetchApplicant();
          successCvUpload();
        } catch (e: any) {
          generalError();
        }
      }}
      accept=".doc,.docx,.pdf"
      maxCount={1}
      beforeUpload={beforeUpload}
      showUploadList={false}
    >
      <div className={styles.wrapCvCard}>
        <div className={styles.wrapCvInfo}>
          <div className={styles.cvTitle}>
            <div className={`main-body-text ${styles.cvInfoText}`}>{translate('no_cv')}</div>
          </div>
        </div>
        <div className={styles.wrapEmptyCvIcon}>
          <Image src="/assets/images/empty-cv.png" preview={false} alt="Empty cv" />
        </div>
      </div>
    </Upload>
  );
};

export default CvCard;
